import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Search, UserPlus, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '../lib/auth';
import { toast } from 'sonner';

/**
 * Türkçe-uyumlu arama normalizasyonu — diakritik kaldırır, küçük harfe
 * çevirir, "ı/I" ve "i/İ" ikiliğini eşitler. Operatör "sigirtmac" yazınca
 * "Sığırtmaç" da yakalanır, "ali" yazınca "Alı" da gelir.
 */
function normalizeTr(s) {
  if (!s) return '';
  return String(s)
    .toLocaleLowerCase('tr')
    .replace(/[ığ]/g, 'g')
    .replace(/[üû]/g, 'u')
    .replace(/[şŝ]/g, 's')
    .replace(/[ı]/g, 'i')
    .replace(/[öô]/g, 'o')
    .replace(/[çĉ]/g, 'c')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Plaka ↔ Çalışan atama dialog'u.
 *
 * Akış:
 *   1. Operatör araç satırındaki "person+" ikonuna tıklar → bu dialog açılır.
 *   2. Mevcut atama varsa onay banner'ında gösterilir.
 *   3. Operatör arama kutusu ile çalışan filtreler (TR-uyumlu).
 *   4. Bir kişi seçer → opsiyonel not yazar → "Ata" butonuna basar.
 *   5. Eski atama otomatik kapatılır, yenisi açılır, history kaydı atılır.
 */
export function VehicleAssignDialog({ open, onClose, vehicle, onAssigned }) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [note, setNote] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);

  // Reset on open + initial fetch
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedId(null);
    setNote('');
    setLoading(true);
    Promise.all([
      api.get('/planning/employees'),
      api.get(`/vehicles/${vehicle.id}/assignment-history`),
    ])
      .then(([empRes, histRes]) => {
        const items = (empRes.data?.data || []).filter(
          (e) => (e.email || '').toLowerCase() !== 'admin@hrportal.be'
        );
        setEmployees(items);
        const active = (histRes.data?.data || []).find((a) => !a.unassigned_at);
        setCurrentAssignment(active || null);
      })
      .catch(() => toast.error('Çalışan listesi yüklenemedi'))
      .finally(() => setLoading(false));
  }, [open, vehicle?.id]);

  const filtered = useMemo(() => {
    const q = normalizeTr(search.trim());
    if (!q) return employees;
    return employees.filter((e) => {
      const blob = `${e.full_name || ''} ${e.email || ''} ${e.firm_name || ''} ${e.depo || ''}`;
      return normalizeTr(blob).includes(q);
    });
  }, [employees, search]);

  const handleAssign = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/vehicles/${vehicle.id}/assign`, {
        user_id: selectedId,
        note: note.trim() || undefined,
      });
      toast.success(`${res.data.data.user_full_name} → ${vehicle.license_plate} atandı`);
      onAssigned?.(res.data.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Atama yapılamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassign = async () => {
    if (!currentAssignment) return;
    setSubmitting(true);
    try {
      await api.delete(`/vehicles/${vehicle.id}/assign`);
      toast.success('Atama kaldırıldı');
      onAssigned?.(null);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'İşlem yapılamadı');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !vehicle) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[500px]" data-testid="vehicle-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-500" />
            Plaka Ata — <span className="font-mono text-base bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">{vehicle.license_plate}</span>
          </DialogTitle>
          <DialogDescription>
            {vehicle.brand} {vehicle.model} {vehicle.firm_name ? `· ${vehicle.firm_name}` : ''}
          </DialogDescription>
        </DialogHeader>

        {currentAssignment && (
          <div className="border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/50 rounded p-2.5 flex items-start gap-2 text-xs" data-testid="vehicle-assign-current">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-bold text-amber-800 dark:text-amber-200">
                Şu an atanmış: {currentAssignment.user_full_name}
              </div>
              <div className="text-[11px] text-amber-700 dark:text-amber-300/80">
                {new Date(currentAssignment.assigned_at).toLocaleDateString('tr')} tarihinden bu yana
                {currentAssignment.note ? ` · "${currentAssignment.note}"` : ''}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                Yeni atama yapılırsa eski atama otomatik kapanır.
              </div>
            </div>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İsim, e-posta, firma veya depo ara…"
            className="pl-8 h-9 text-sm"
            data-testid="vehicle-assign-search"
            autoFocus
          />
        </div>

        <div className="max-h-72 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-100 dark:divide-slate-700/50" data-testid="vehicle-assign-employee-list">
          {loading ? (
            <div className="py-8 text-center text-xs text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
              Yükleniyor…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400 italic">
              {search ? 'Eşleşme yok' : 'Çalışan yok'}
            </div>
          ) : (
            filtered.slice(0, 100).map((emp) => {
              const sel = selectedId === emp.id;
              return (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => setSelectedId(emp.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/40 flex items-center gap-2 ${sel ? 'bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-400' : ''}`}
                  data-testid={`vehicle-assign-pick-${emp.id}`}
                >
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${sel ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                    {(emp.full_name || '?').split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 dark:text-slate-100 truncate">{emp.full_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {emp.firm_name || '—'}{emp.depo ? ` · ${emp.depo}` : ''}
                      {emp.role && emp.role !== 'EMPLOYEE' && (
                        <span className="ml-1 px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[9px] uppercase font-bold">{emp.role}</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
          {filtered.length > 100 && (
            <div className="py-2 text-center text-[10px] text-slate-400 italic">
              {filtered.length - 100} sonuç daha — daha spesifik arama yapın
            </div>
          )}
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Not (opsiyonel)</label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Örn: Çağrı vardiyası için"
            className="text-sm h-16 resize-none"
            data-testid="vehicle-assign-note"
          />
        </div>

        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          {currentAssignment ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnassign}
              disabled={submitting}
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="vehicle-assign-unassign"
            >
              Atamayı Kaldır
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              İptal
            </Button>
            <Button
              size="sm"
              onClick={handleAssign}
              disabled={!selectedId || submitting}
              data-testid="vehicle-assign-confirm"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserPlus className="h-3.5 w-3.5 mr-1" />}
              {currentAssignment ? 'Yeniden Ata' : 'Ata'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
